import { useMemo } from 'react';
import { useNode } from '@craftjs/core';
import Select from '@mui/material/Select';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  FormatAlignLeft as AlignLeftIcon,
  FormatAlignCenter as AlignCenterIcon,
  FormatAlignRight as AlignRightIcon,
  FormatBold as BoldIcon,
} from '@mui/icons-material';
import ItalicIcon from '@mui/icons-material/FormatItalic';
import UnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import NewColorPicker from '@/components/editor/settings/ColorSettings';
import useResponsiveValues, { ResponsiveValue } from '@/components/editor/hooks/useResponsiveValues';

export enum TextHtmlTag {
  H1 = 'h1',
  H2 = 'h2',
  H3 = 'h3',
  P = 'p',
  SMALL = 'small',
}

export enum TextAlign {
  LEFT = 'left',
  CENTER = 'center',
  RIGHT = 'right',
}

export enum FontSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  X_LARGE = 'x-large',
  XX_LARGE = 'xx-large',
  XXX_LARGE = 'xxx-large',
}

export enum FontWeight {
  NORMAL = 'normal',
  BOLD = 'bold',
}

export enum FontStyle {
  NORMAL = 'normal',
  ITALIC = 'italic',
}

export enum TextDecoration {
  UNDERLINE = 'underline',
  OVERLINE = 'overline',
  NONE = 'none',
}

export interface Font {
  fontSize?: FontSize | ResponsiveValue<FontSize>;
  textAlign?: TextAlign | ResponsiveValue<TextAlign>;
  tag?: TextHtmlTag | ResponsiveValue<TextHtmlTag>;
  textDecoration?: TextDecoration | ResponsiveValue<TextDecoration>;
  fontWeight?: FontWeight | ResponsiveValue<FontWeight>;
  fontStyle?: FontStyle | ResponsiveValue<FontStyle>;
  fontFamily?: string | ResponsiveValue<string>;
  color?: string | ResponsiveValue<string>;
}

const TextSettings = () => {
  const {
    props,
    actions: { setProp },
  } = useNode((node) => ({
    props: node.data.props,
  }));

  const responsive = useResponsiveValues();

  const textFormatValues = useMemo(() => {
    const values = [
      responsive.getValue(props?.fontWeight), 
      responsive.getValue(props?.fontStyle), 
      responsive.getValue(props?.textDecoration)
    ];

    return values.filter((f) => !!f)
  }, [responsive, props?.fontWeight, props?.fontStyle, props?.textDecoration]);

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <InputLabel>Tamaño</InputLabel>
        <ToggleButtonGroup
          color="primary"
          value={responsive.getValue(props.fontSize)}
          onChange={(_, value) => {
            setProp((props: Font) => {
              props.fontSize = responsive.setValue(props.fontSize, value);
              return props;
            });
          }}
          exclusive
          fullWidth
          size="small"
        >
          <ToggleButton value={FontSize.SMALL}>S</ToggleButton>
          <ToggleButton value={FontSize.MEDIUM}>R</ToggleButton>
          <ToggleButton value={FontSize.X_LARGE}>L</ToggleButton>
          <ToggleButton value={FontSize.XXX_LARGE}>XL</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Stack spacing={2} direction="row">
        <Stack spacing={1} flex={1}>
          <InputLabel>Formato</InputLabel>
          <ToggleButtonGroup
            color="primary"
            value={textFormatValues}
            onChange={(_, value) => {
              setProp((props: Font) => {
                if (Object.values(FontWeight).includes(value)) {
                  props.fontWeight = responsive.toggleValue(props.fontWeight, value);
                }
                if (Object.values(FontStyle).includes(value)) {
                  props.fontStyle = responsive.toggleValue(props.fontStyle, value);
                }
                if (Object.values(TextDecoration).includes(responsive.getValue(value))) {
                  props.textDecoration = responsive.toggleValue(props.textDecoration, value);
                }
                return props;
              });
            }}
            exclusive
            fullWidth
            size="small"
          >
            <ToggleButton value={FontWeight.BOLD}>
              <BoldIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value={FontStyle.ITALIC}>
              <ItalicIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value={TextDecoration.UNDERLINE}>
              <UnderlinedIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Stack spacing={1} flex={1}>
          <InputLabel>Alineación</InputLabel>
          <ToggleButtonGroup
            color="primary"
            value={responsive.getValue(props.textAlign)}
            onChange={(_, value) => {
              setProp((props: Font) => {
                props.textAlign = responsive.setValue(props.textAlign, value);
                return props;
              });
            }}
            exclusive
            fullWidth
            size="small"
          >
            <ToggleButton value={TextAlign.LEFT}>
              <AlignLeftIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value={TextAlign.CENTER}>
              <AlignCenterIcon fontSize="small" />
            </ToggleButton>
            <ToggleButton value={TextAlign.RIGHT}>
              <AlignRightIcon fontSize="small" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
      <FormControl fullWidth>
        <InputLabel id="text-html-tag">Tipo</InputLabel>
        <Select
          label="Tipo"
          value={responsive.getValue(props.tag)}
          size="small"
          onChange={(e) => {
            setProp((props: Font) => {
              props.tag = responsive.setValue(props.tag, e.target.value);
              props.fontSize = responsive.setValue();
              return props;
            });
          }}
        >
          <MenuItem value={TextHtmlTag.H1}>Título 1</MenuItem>
          <MenuItem value={TextHtmlTag.H2}>Título 2</MenuItem>
          <MenuItem value={TextHtmlTag.H3}>Título 3</MenuItem>
          <MenuItem value={TextHtmlTag.P}>Párrafo</MenuItem>
          <MenuItem value={TextHtmlTag.SMALL}>Descripción</MenuItem>
        </Select>
      </FormControl>
      <NewColorPicker field="color" />
    </Stack>
  );
};

export default TextSettings;
